import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import partition from 'lodash/partition'

import {
  Button,
  CriticalIcon,
  EmptyState,
  Flex,
  Heading,
  Notice,
  PlusIcon,
  Skeleton,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { StartPageSunsetNotice } from '~publish/components/StartPageSunsetNotice'
import {
  useAccount,
  useHasOrganizationAction,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { openChannelStorefront } from '~publish/helpers/channelConnections'
import { isTrialUser } from '~publish/helpers/user'

import { ChannelRefreshNotices } from './components/ChannelRefreshNotices'
import ChannelRow from './components/ChannelRow'
import ChannelsCounterBar from './components/ChannelsCounterBar'
import ChannelsPricingNotice from './components/ChannelsPricingNotice'
import { useLinkFacebookPageHints } from './components/LinkFacebookPageCoachmark'
import { useChannelsForSettings } from './hooks/useChannelsForSettings'
import { isChannelRequiringRefresh } from './utils/getChannelStatus'
import { AddOrRemoveChannelsDialog } from '../shared/components/AddOrRemoveChannelsDialog'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { shouldShowAddRemoveChannels } from '../shared/utils/channelPermissions'
import { isStartPage } from '../shared/utils/isStartPage'

import style from './Channels.module.css'

import type { JSX } from 'react'

const AddOrRemoveChannelsButton = (): JSX.Element | null => {
  const { account } = useAccount()
  const [showDialog, setShowDialog] = React.useState(false)
  const shouldShow = shouldShowAddRemoveChannels(account)
  // The dialog changes the subscription quantity, so it is a billing action,
  // not a channel one.
  const canManageBilling = useHasOrganizationAction('manageBilling')

  if (!shouldShow || !canManageBilling) {
    return null
  }

  return (
    <>
      <Button
        size="medium"
        variant="secondary"
        onClick={() => setShowDialog(true)}
      >
        Add or Remove Channels
      </Button>

      <AddOrRemoveChannelsDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        cta="settings-channels-addRemove-1"
      />
    </>
  )
}

const ConnectChannelButton = ({
  context,
}: {
  context: 'main' | 'emptyState'
}): JSX.Element | null => {
  const canConnectChannel = useHasOrganizationAction('manageChannels')

  if (!canConnectChannel) {
    return null
  }

  return (
    <Button
      size="medium"
      variant="primary"
      onClick={(): void =>
        openChannelStorefront({
          cta: 'account-channels-pageHeader-connectChannel-1',
        })
      }
      data-testid={`connect-channel-button-${context}`}
    >
      Connect Channel
    </Button>
  )
}

const ContactAdminNotice = (): JSX.Element | null => {
  const canManageChannels = useHasOrganizationAction('manageChannels')

  if (canManageChannels) {
    return null
  }

  return (
    <Notice>Contact an Admin in your organization to manage channels.</Notice>
  )
}

const Channels = ({
  preOpenChannelStorefront = false,
}: {
  preOpenChannelStorefront?: boolean
}): JSX.Element => {
  const { account } = useAccount()
  const organizationId = useOrganizationId()
  const isOnTrial = isTrialUser(account)
  const isMobile = useIsMobileScreenSize()

  const [, setSearchParams] = useSearchParams()
  const {
    channels,
    loading: loadingChannels,
    error: errorLoadingChannels,
    refetchChannels,
  } = useChannelsForSettings({
    organizationId,
  })

  const [lockedChannels, connectedChannels] = partition(
    channels,
    (c) => c.isLocked,
  )
  const [refreshRequiredConnectedChannels, otherConnectedChannels] = partition(
    connectedChannels,
    isChannelRequiringRefresh,
  )
  const orderedConnectedChannels = [
    ...refreshRequiredConnectedChannels,
    ...otherConnectedChannels,
  ]

  const {
    hints: linkFacebookPageHints,
    dismiss: dismissLinkFacebookPageHints,
  } = useLinkFacebookPageHints(orderedConnectedChannels)

  const lockedChannelsHeader = React.useRef<HTMLHeadingElement>(null)
  const [lockedChannelsVisible, setLockedChannelsVisible] =
    React.useState(false)

  // track if lockedChannelsHeader is in view
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setLockedChannelsVisible(true)
          } else {
            setLockedChannelsVisible(false)
          }
        })
      },
      { threshold: 0.85 },
    )
    if (lockedChannelsHeader.current)
      observer.observe(lockedChannelsHeader.current)
    return (): void => {
      observer.disconnect()
    }
  }, [lockedChannelsHeader])

  // Skeleton loading view
  if (!account?.currentOrganization || loadingChannels) {
    return (
      <NewSettingsPageLayout.Root pageTitle="Settings - Channels">
        <Flex as="header" fullWidth>
          <NewSettingsPageLayout.Title>Channels</NewSettingsPageLayout.Title>
        </Flex>
        <NewSettingsPageLayout.Content gap="xl">
          <Skeleton>
            <Heading as="h2" size="small">
              Connected Channels
            </Heading>
          </Skeleton>
          <ul className={style.channelList}>
            {[0, 1, 2].map((index) => (
              <li key={index}>
                <Skeleton width="100%" height="74px" />
              </li>
            ))}
          </ul>
        </NewSettingsPageLayout.Content>
      </NewSettingsPageLayout.Root>
    )
  }

  const channelsLimit = account.currentOrganization.limits.channels
  const channelsConnected = channels?.length ?? 0
  const channelsConnectedOrMax = Math.min(channelsConnected, channelsLimit ?? 0)

  if (errorLoadingChannels) {
    return (
      <NewSettingsPageLayout.Root pageTitle="Settings - Channels">
        <Flex as="header" fullWidth>
          <NewSettingsPageLayout.Title>Channels</NewSettingsPageLayout.Title>
        </Flex>
        <NewSettingsPageLayout.Content gap="xl">
          <EmptyState variant="critical">
            <EmptyState.Icon>
              <CriticalIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>Failed to load channels</EmptyState.Heading>
            <EmptyState.Description>
              Something went wrong. Please try again later.
            </EmptyState.Description>
          </EmptyState>
        </NewSettingsPageLayout.Content>
      </NewSettingsPageLayout.Root>
    )
  }

  if (preOpenChannelStorefront && !loadingChannels && account) {
    setTimeout(() => {
      openChannelStorefront({
        cta: 'account-channels-noClick-redirect-1',
      })
      setSearchParams({}) // Clear the search params
      // Delay opening the modal to avoid empty state flicker
    }, 500)
  }

  return (
    <NewSettingsPageLayout.Root pageTitle="Settings - Channels">
      <Flex
        as="header"
        gap="sm"
        direction={isMobile ? 'column' : 'row'}
        align="start"
        justify={isMobile ? 'start' : 'between'}
        fullWidth
      >
        <NewSettingsPageLayout.Title>Channels</NewSettingsPageLayout.Title>
        <Flex
          gap="xs"
          direction="row"
          align="center"
          justify={isMobile ? 'start' : 'end'}
          wrap="wrap"
          fullWidth={isMobile}
        >
          <AddOrRemoveChannelsButton />
          <ConnectChannelButton context="main" />
        </Flex>
      </Flex>
      <NewSettingsPageLayout.Content gap="xl">
        {/* Banner with current plan and limit */}
        <ChannelsPricingNotice
          numLockedChannels={lockedChannels.length}
          lockedChannelsVisible={lockedChannelsVisible}
        />

        <ContactAdminNotice />

        <ChannelRefreshNotices channels={connectedChannels} />

        {channels?.some((channel) => isStartPage({ channel })) && (
          <StartPageSunsetNotice />
        )}

        {channels && channels.length > 0 ? (
          <>
            <Flex gap="sm" direction="column" fullWidth as="section">
              <Flex direction="row" align="center" justify="between" fullWidth>
                <Heading as="h2" size="small">
                  {channelsConnectedOrMax}/
                  {isOnTrial
                    ? 'Unlimited'
                    : `${channelsLimit} Channel${
                        channelsLimit === 1 ? '' : 's'
                      } connected`}
                </Heading>
                <ChannelsCounterBar
                  connected={channelsConnectedOrMax}
                  limit={channelsLimit}
                />
              </Flex>
              <ul className={style.channelList}>
                {orderedConnectedChannels.map((channel) => (
                  <li key={channel.id}>
                    <ChannelRow
                      channel={channel}
                      onChannelUpdated={refetchChannels}
                      linkFacebookPageHint={linkFacebookPageHints.get(
                        channel.id,
                      )}
                      onLinkFacebookPageHintDismiss={
                        dismissLinkFacebookPageHints
                      }
                    />
                  </li>
                ))}
              </ul>
            </Flex>
            {lockedChannels.length > 0 && (
              <Flex gap="sm" direction="column" fullWidth as="section">
                <Heading
                  as="h2"
                  size="small"
                  id="locked-channels"
                  ref={lockedChannelsHeader}
                >
                  Locked Channels
                </Heading>
                <ul className={style.channelList}>
                  {lockedChannels.map((channel) => (
                    <li key={channel.id}>
                      <ChannelRow
                        channel={channel}
                        onChannelUpdated={refetchChannels}
                      />
                    </li>
                  ))}
                </ul>
              </Flex>
            )}
          </>
        ) : (
          <EmptyState size="medium">
            <EmptyState.Icon>
              <PlusIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>
              Connect a channel to get started
            </EmptyState.Heading>
            <EmptyState.Description>
              Once connected, you&apos;ll see your channels listed here.
            </EmptyState.Description>
            <EmptyState.Actions>
              <ConnectChannelButton context="emptyState" />
            </EmptyState.Actions>
          </EmptyState>
        )}
      </NewSettingsPageLayout.Content>
    </NewSettingsPageLayout.Root>
  )
}

export default Channels
